/**
 * Modal component for viewing detailed integration information
 * Shows configuration, current status, error details, and run history
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Box,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { withStyles } from '@material-ui/core/styles';
import { appUrl } from '../../../../../utils/config';

// Material-UI styles for the integration modal layout and components
const styles = (theme) => ({
  dialog: {
    '& .MuiDialog-container': {
      justifyContent: 'flex-end',
      alignItems: 'stretch',
    },
    '& .MuiDialog-paper': {
      margin: 0,
      maxWidth: '50vw',
      width: '50vw',
      maxHeight: '100vh',
      height: '100vh',
      borderRadius: 0,
      position: 'fixed',
      right: 0,
      top: 0,
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
     '& .MuiTypography-h6': {
      display: 'flex',
      justifyContent: 'space-between',
      flexGrow: 1,
    },
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 500,
    color: '#253239',
  },
  subtitle: {
    fontSize: '1rem',
    color: '#666',
    marginTop: '4px',
  },
  closeButton: {
    color: '#666',
  },
  content: {
    padding: '24px',
    height: 'calc(100vh - 80px)',
    overflowY: 'auto',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#253239',
    marginBottom: '16px',
  },
  configCard: {
    marginBottom: '24px',
    border: 'none',
    boxShadow: 'none',
    // backgroundColor: '#F9F9FC',
    '& .MuiCardContent-root': {
      padding: 0,
    },
  },
  configLabel: {
    fontSize: '0.75rem',
    color: '#666',
    fontWeight: 500,
    marginBottom: '4px',
    letterSpacing: '0.5px',
  },
  configValue: {
   fontSize: '0.95rem',
    color: '#253239',
    fontWeight: 500,
  },
  statusCard: {
    marginBottom: '24px',
    border: 'none',
    boxShadow: 'none',
    // backgroundColor: '#F9F9FC',
     '& .MuiCardContent-root': {
      padding: 0,
    },
  },
  errorCard: {
    backgroundColor: '#FFE4CC',
    border: '1px solid #FFA95E',
    marginBottom: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
  },
  errorTitle: {
    fontSize: '0.875rem',
    marginBottom: '4px',
  },
  errorMessage: {
    fontSize: '0.875rem',
  },
  table: {
    border: 'none',
  },
  tableHeader: {
    backgroundColor: '#F9F9FC',
    '& th': {
      fontWeight: 600,
      color: '#253239',
      borderBottom: 'none',
      padding: '12px 16px',
    },
  },
  tableRow: {
    '& td': {
      padding: '12px 16px',
      borderBottom: '1px solid #DBDBDB',
    },
  },
  statusChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
    padding: '4px 8px',
    borderRadius: '4px',
    textAlign: 'center',
    minWidth: '70px',
  },
  successChip: {
    backgroundColor: '#4CA30D',
    color: '#ffffff',
  },
  failedChip: {
    backgroundColor: '#FF0000',
    color: '#ffffff',
  },
  runningChip: {
    backgroundColor: '#EC6535',
    color: '#ffffff',
  },
  retryingChip: {
    backgroundColor: '#9B1515',
    color: '#ffffff',
  },
});

const ViewIntegrationModal = ({ classes, open, onClose, integration }) => {
  const [integrationDetails, setIntegrationDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  // Make API call when modal opens with integration ID
  useEffect(() => {
    if (open && integration && integration.integrationId) {
      const fetchIntegrationDetails = async () => {
        setLoading(true);
        try {
          const payload = {
            integrationId: integration.integrationId
          };
          const response = await axios.post(`${appUrl}/api/cron/dashboard`, payload);

          if (response.data && response.data.integration) {
            setIntegrationDetails(response.data.integration);
          }
        } catch (error) {
          console.error('Integration Details API Error:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchIntegrationDetails();
    } else {
      setIntegrationDetails(null);
    }
  }, [open, integration]);

  if (!integration) return null;

  // Generate run history status chips with color coding
  const getStatusChip = (status) => {
    let chipClasses = [classes.statusChip];

    // Apply appropriate styling based on status type
    switch (status.toLowerCase()) {
      case 'success':
        chipClasses.push(classes.successChip);
        break;
      case 'failed':
        chipClasses.push(classes.failedChip);
        break;
      case 'running':
        chipClasses.push(classes.runningChip);
        break;
      case 'retrying':
        chipClasses.push(classes.retryingChip);
        break;
      default:
        break;
    }

    return <Chip label={status} className={chipClasses.join(' ')} size="small" />;
  };

  // Get run history from API or fallback to mock data
  const runHistory = integrationDetails?.executionHistory || [
    {
      runTime: integration.lastRun || '14-JUN-2025 14:00',
      status: integration.status || 'Success',
      duration: integration.duration || '00:30',
      errorCode: '-',
      retry: integration.retryCount || 0,
      ASNData: integration.ASNData || 'No data',
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className={classes.dialog}
      TransitionProps={{
        timeout: 300,
      }}
      PaperProps={{
        style: {
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 300ms ease-in-out',
        },
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <div>
          <Typography className={classes.title}>
            View Integration
          </Typography>
          <Typography className={classes.subtitle}>
            {integration.integrationName}
          </Typography>
        </div>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {/* Configuration Section */}
        <Typography className={classes.sectionTitle}>
          Configuration
        </Typography>

        <Card className={classes.configCard}>
          <CardContent className={classes.configGrid}>
            <Grid container spacing={2}>
              {/* <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Type</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.configuration?.type || integration.type || 'N/A'}
                </Typography>
              </Grid> */}
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Source</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.configuration?.source || integration.sourceSystem || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Target</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.configuration?.target || integration.targetSystem || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Schedule</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.configuration?.schedule || 'Every 15 minutes'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Current Status Section */}
        <Typography className={classes.sectionTitle}>
          Current Status
        </Typography>

        <Card className={classes.statusCard}>
          <CardContent className={classes.configGrid}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Status</Typography>
                <Typography className={classes.configValue} style={{
                  color: (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'failed' ? '#FF0000' :
                         (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'success' ? '#4CA30D' :
                         (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'Success' ? '#4CA30D' :
                         (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'running' ? '#EC6535' :
                         (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'retrying' ? '#9B1515' : '#666',
                  fontWeight: 500
                }}>
                  {(integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'success' ? 'Success' :
                   (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'failed' ? 'Failed' :
                   (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'running' ? 'Running' :
                   (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) === 'retrying' ? 'Retrying' :
                   (integrationDetails?.executionHistory?.[0]?.status || integrationDetails?.currentStatus?.status || integration.status) || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Last Run</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.currentStatus?.lastRun ?
                    new Date(integrationDetails.currentStatus.lastRun).toLocaleString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : integration.lastRun || 'N/A'
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Duration</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.currentStatus?.duration || integration.duration || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Typography className={classes.configLabel}>Retry Count</Typography>
                <Typography className={classes.configValue}>
                  {integrationDetails?.currentStatus?.retryCount || integration.retryCount || 0}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Error Message (only show for failed integrations) */}
        {(integrationDetails?.currentStatus?.status === 'failed' || integration.status === 'Failed') && (
          <Box className={classes.errorCard}>
            <Typography className={classes.errorTitle}>
              <span style={{ fontWeight: 'bold' }}>Error Code:</span> {integrationDetails?.currentStatus?.errorCode || 'HTTP 504'}
            </Typography>
            <Typography className={classes.errorMessage}>
              <span style={{ fontWeight: 'bold' }}>Message:</span> {integrationDetails?.currentStatus?.errorMessage || 'Gateway timeout during bulk sync operation'}
            </Typography>
          </Box>
        )}

        {/* Run History Table */}
        <Typography className={classes.sectionTitle} style={{ marginTop: '24px' }}>
          Run History
        </Typography>

        <Table className={classes.table}>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>Run Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Error Code</TableCell>
              <TableCell>Retry</TableCell>
              <TableCell>Response</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2">Loading data...</Typography>
                </TableCell>
              </TableRow>
            ) : runHistory.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2">No execution history available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              runHistory.map((run, index) => (
                <TableRow key={index} className={classes.tableRow}>
                  <TableCell>
                    <Typography variant="body2">
                      {run.runTime ? new Date(run.runTime).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : run.runTime}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(run.status === 'success' ? 'Success' :
                            run.status === 'failed' ? 'Failed' :
                            run.status === 'running' ? 'Running' :
                            run.status === 'retrying' ? 'Retrying' : run.status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {run.duration || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {run.errorCode || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {run.retry || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {run.ASNData || 'No data'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

export default withStyles(styles)(ViewIntegrationModal);
